/**
 * Native OAuth Helper
 *
 * Handles OAuth flow on Capacitor (iOS/Android) using the in-app browser.
 * On native platforms, OAuth opens in an in-app browser (ASWebAuthenticationSession
 * on iOS, Chrome Custom Tabs on Android). The callback uses a custom URL scheme
 * (com.duneworks.app://) so the OS routes it back to the app.
 *
 * On web, this module is a no-op — the regular window.location.href flow is used.
 */

import { isCapacitor } from "../../lib/platform";

// Custom URL scheme matching the appId in capacitor.config.ts
const APP_SCHEME = "com.duneworks.app";

/**
 * Get the native redirect URI for a given provider.
 * Uses custom URL scheme so the OS routes the callback back to the app.
 */
export function getNativeRedirectUri(provider: "google" | "m365"): string {
  return `${APP_SCHEME}://auth/${provider}/callback`;
}

/**
 * Open a URL in the in-app browser (native) or via window.location (web).
 * Returns true if handled natively, false if the caller should use window.location.
 */
export async function openOAuthUrl(url: string): Promise<boolean> {
  if (!isCapacitor()) {
    return false; // Let the caller handle it via window.location.href
  }

  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({
      url,
      presentationStyle: "popover",
      windowName: "_self",
    });
    return true;
  } catch (error) {
    console.error("Failed to open in-app browser, falling back to redirect:", error);
    return false;
  }
}

/**
 * Close the in-app browser (call after receiving the callback).
 */
export async function closeOAuthBrowser(): Promise<void> {
  if (!isCapacitor()) return;

  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.close();
  } catch (error) {
    console.error("Failed to close in-app browser:", error);
  }
}

/**
 * Register a one-time listener for the OAuth callback via deep link.
 * Returns a promise that resolves with the callback URL when the OS
 * routes the custom URL scheme back to the app.
 */
export function listenForOAuthCallback(): Promise<string> | null {
  if (!isCapacitor()) return null;

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("OAuth callback timed out (120s)"));
    }, 120000);

    import("@capacitor/app").then(({ App }) => {
      const listener = App.addListener("appUrlOpen", (event) => {
        clearTimeout(timeout);
        listener.then((handle) => handle.remove());
        resolve(event.url);
      });
    }).catch(reject);
  });
}

/**
 * Parse an OAuth callback URL (either custom scheme or https).
 * Handles both hash fragments (#access_token=...) and query params (?code=...).
 */
export function parseOAuthCallbackUrl(callbackUrl: string): {
  provider: "google" | "m365" | null;
  params: URLSearchParams;
} {
  // Determine provider from URL path
  let provider: "google" | "m365" | null = null;
  if (callbackUrl.includes("/auth/google")) {
    provider = "google";
  } else if (callbackUrl.includes("/auth/m365")) {
    provider = "m365";
  }

  // Parse hash fragment (implicit flow returns tokens in hash)
  let params: URLSearchParams;
  const hashIndex = callbackUrl.indexOf("#");
  if (hashIndex !== -1) {
    params = new URLSearchParams(callbackUrl.substring(hashIndex + 1));
  } else {
    // Fallback to query params
    const queryIndex = callbackUrl.indexOf("?");
    params = queryIndex !== -1
      ? new URLSearchParams(callbackUrl.substring(queryIndex + 1))
      : new URLSearchParams();
  }

  return { provider, params };
}
